import { useEffect } from "react";
import { Loader2, X, AlertTriangle } from "lucide-react";

/**
 * ConfirmModal — async, state-driven replacement for window.confirm.
 *
 * Strike 8C standardised confirmation dialog. Matches the enterprise dark-mode
 * design language established by Stage 8B's NewListModal: centered overlay,
 * `fixed inset-0 z-50 flex items-center justify-center p-4`, dark-charcoal
 * surface, Cancel + primary action footer, ESC to dismiss.
 *
 * Props:
 *   open          — controls visibility.
 *   onClose       — invoked on Cancel, backdrop click, ESC.
 *   onConfirm     — async or sync. The caller awaits any mutation it kicks
 *                   off; the modal stays mounted (with `busy` spinner) until
 *                   the caller flips `busy` back to false or unmounts via
 *                   `onClose`.
 *   title         — heading text (required).
 *   description   — supporting body copy. Plain string or React node.
 *   confirmLabel  — primary button label. Defaults to "Confirm".
 *   cancelLabel   — defaults to "Cancel".
 *   variant       — "destructive" (red) | "default" (white). Defaults to
 *                   "destructive" since 95 % of usages are destructive.
 *   busy          — disable the buttons + show a spinner while the parent's
 *                   mutation is in-flight.
 *   testId        — root data-testid hook (defaults to "confirm-modal").
 *   kicker        — optional small uppercase eyebrow above the title (e.g.,
 *                   "Inbox · Disconnect"). Mirrors the NewListModal eyebrow.
 */
export default function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "destructive",
  busy = false,
  testId = "confirm-modal",
  kicker,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, busy]);

  if (!open) return null;

  const isDestructive = variant === "destructive";
  const primaryClasses = isDestructive
    ? "bg-red-500/90 text-white hover:bg-red-500"
    : "bg-white text-black hover:bg-neutral-200";

  return (
    <div
      data-testid={testId}
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <button
        type="button"
        aria-label="Close"
        data-testid={`${testId}-overlay`}
        className="absolute inset-0 bg-black/65 backdrop-blur-sm"
        onClick={busy ? undefined : onClose}
      />
      <div className="relative w-full max-w-md bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg shadow-2xl overflow-hidden">
        <div className="flex items-start justify-between px-6 py-4 border-b border-[#1a1a1a]">
          <div className="flex items-start gap-3">
            {isDestructive && (
              <div
                className="mt-0.5 w-8 h-8 rounded-md bg-red-500/10 border border-red-500/20 flex items-center justify-center shrink-0"
                aria-hidden
              >
                <AlertTriangle
                  className="w-4 h-4 text-red-400"
                  strokeWidth={1.8}
                />
              </div>
            )}
            <div className="min-w-0">
              {kicker && (
                <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-neutral-500">
                  {kicker}
                </div>
              )}
              <h2 className="font-display text-xl font-semibold tracking-tight mt-1">
                {title}
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid={`${testId}-close`}
            aria-label="Close"
            className="p-2 text-neutral-500 hover:text-white hover:bg-[#161616] rounded-md transition-colors disabled:opacity-30 -mr-2"
          >
            <X className="w-4 h-4" strokeWidth={1.8} />
          </button>
        </div>

        {description && (
          <div className="px-6 py-5 text-sm text-neutral-300 leading-relaxed">
            {description}
          </div>
        )}

        <div className="px-6 py-4 border-t border-[#1a1a1a] flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid={`${testId}-cancel`}
            className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-400 hover:text-white px-3 py-2 disabled:opacity-30"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid={`${testId}-confirm`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-40 ${primaryClasses}`}
          >
            {busy && (
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
            )}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
